import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import {
  CheckCircle2,
  Zap,
  ArrowRight,
  QrCode,
  BookOpen,
  GraduationCap,
  Briefcase,
  Mail,
  MessageSquare,
  Globe,
  Clock,
  Sparkles,
  Layers,
  FileText,
} from "lucide-react";

export const NixlevConcept1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const greenDark = "#0B1A12";
  const greenForest = "#1B3B2B";
  const greenEmerald = "#10B981";
  const goldAccent = "#D4A346";

  const SUBTITLES = [
    { from: 0, to: 90, text: "Ini cara baru menjual karya digital di internet.", highlight: "cara baru" },
    { from: 90, to: 235, text: "Nixlev adalah storefront instan untuk kreator & freelancer modern.", highlight: "storefront instan" },
    { from: 235, to: 300, text: "Satu halaman bersih dan elegan untuk semua penawaranmu.", highlight: "Satu halaman bersih" },
    { from: 300, to: 420, text: "E-book, template, kelas video, hingga sesi konsultasi.", highlight: "E-book, template, kelas, jasa" },
    { from: 420, to: 510, text: "Tanpa sewa hosting. Tanpa koding yang membingungkan.", highlight: "Tanpa sewa hosting" },
    { from: 510, to: 690, text: "Pembeli cukup scan QRIS, pembayaran terverifikasi detik itu juga.", highlight: "scan QRIS" },
    { from: 690, to: 870, text: "Sistem cerdas Nixlev langsung mengirimkan file ke WA & Email.", highlight: "ke WA & Email" },
    { from: 870, to: 1080, text: "Otomatis 24 jam nonstop, tanpa kamu perlu sentuh layar.", highlight: "24 jam nonstop" },
    { from: 1080, to: 1260, text: "Semua data, kendali, dan pendapatan—100% milikmu.", highlight: "100% milikmu" },
    { from: 1260, to: 1530, text: "Selamat datang di era baru. Buat tokomu di nixlev.com 🚀", highlight: "nixlev.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: greenDark,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      {/* Ambient Radial Spotlight */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 50% 25%, rgba(16, 185, 129, 0.18) 0%, transparent 60%)`,
          pointerEvents: "none",
        }}
      />

      <Audio src={staticFile("nixlev_concept1_orus.mp3")} />

      {/* SFX */}
      {frame === 0 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />}
      {frame === 235 && <Audio src={staticFile("click.mp3")} volume={0.6} />}
      {frame === 510 && <Audio src={staticFile("whoosh.mp3")} volume={0.5} />}
      {frame === 690 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 1080 && <Audio src={staticFile("click-soft.mp3")} volume={0.6} />}
      {frame === 1260 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}

      {/* BEAT 1 & 2: MANIFESTO & BENTO GRID HERO (Frames 0 - 510) */}
      {frame < 510 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 26px",
              backgroundColor: "rgba(16, 185, 129, 0.15)",
              border: "1px solid rgba(16, 185, 129, 0.4)",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              color: "#34D399",
              letterSpacing: "0.08em",
              marginBottom: 25,
              textTransform: "uppercase",
            }}
          >
            The Modern Creator Storefront
          </div>

          <h1
            style={{
              fontSize: 60,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.15,
              marginBottom: 40,
              letterSpacing: "-0.03em",
            }}
          >
            Satu Halaman Elegan. <br />
            <span style={{ color: "#34D399" }}>Monetisasi Semua Karyamu.</span>
          </h1>

          {/* 3D Bento Grid Structure */}
          <div
            style={{
              width: "100%",
              maxWidth: 960,
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr",
              gap: 20,
              transform: `perspective(1200px) rotateX(${interpolate(frame, [0, 510], [10, 4])}deg)`,
            }}
          >
            {/* Bento 1: Real Desktop Screenshot Window */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                borderRadius: 24,
                overflow: "hidden",
                height: 580,
                boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  height: 42,
                  backgroundColor: "rgba(0, 0, 0, 0.4)",
                  padding: "0 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#9CA3AF",
                }}
              >
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                  <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                  <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#27C93F" }} />
                </div>
                <span>nixlev.com/@kreator</span>
              </div>
              <Img
                src={staticFile("nixlev_desktop.png")}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "top center",
                }}
              />
            </div>

            {/* Bento 2: 4 Multi-product Stack */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { title: "E-Book Masterclass", tag: "PDF", icon: BookOpen, color: "#3B82F6" },
                { title: "Notion & Canva Kit", tag: "Template", icon: FileText, color: "#8B5CF6" },
                { title: "Video Course", tag: "Video", icon: GraduationCap, color: "#F59E0B" },
                { title: "1-on-1 Mentorship", tag: "Jasa", icon: Briefcase, color: "#10B981" },
              ].map((item, idx) => {
                const IconComp = item.icon;
                return (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.06)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: 18,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      transform: `translateX(${interpolate(
                        frame,
                        [idx * 40, idx * 40 + 40],
                        [40, 0],
                        { extrapolateRight: "clamp" }
                      )}px)`,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        backgroundColor: "rgba(255,255,255,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComp size={24} color={item.color} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{item.title}</div>
                      <div style={{ fontSize: 14, color: "#9CA3AF" }}>{item.tag}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* BEAT 3: QRIS & DUAL DELIVERY PARTICLE DISPATCH (Frames 510 - 1080) */}
      {frame >= 510 && frame < 1080 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 26px",
              backgroundColor: "rgba(212, 163, 70, 0.15)",
              border: "1px solid rgba(212, 163, 70, 0.4)",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              color: goldAccent,
              marginBottom: 20,
              textTransform: "uppercase",
            }}
          >
            Instant QRIS & Auto-Dispatch
          </div>

          <h2
            style={{
              fontSize: 54,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.15,
              marginBottom: 40,
            }}
          >
            Scan Langsung di Layar. <br />
            <span style={{ color: "#34D399" }}>Kirim Otomatis Detik Itu Juga.</span>
          </h2>

          {/* Central Black VIP QRIS Card */}
          <div
            style={{
              width: 380,
              backgroundColor: "#162E20",
              border: `2px solid ${greenEmerald}`,
              borderRadius: 32,
              padding: "30px 24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 0 50px rgba(16, 185, 129, 0.35)",
              marginBottom: 35,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 800, color: "#34D399", marginBottom: 14 }}>
              QRIS PEMBAYARAN INSTAN
            </div>
            <div
              style={{
                width: 220,
                height: 220,
                backgroundColor: "#FFFFFF",
                borderRadius: 20,
                padding: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <QrCode size={190} color="#111827" />
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  height: 5,
                  backgroundColor: greenEmerald,
                  boxShadow: "0 0 20px #10B981",
                  top: `${interpolate(frame % 60, [0, 60], [0, 215])}px`,
                }}
              />
            </div>
            <div style={{ marginTop: 16, fontSize: 20, fontWeight: 800, color: "#34D399" }}>
              ✔ Terverifikasi Otomatis
            </div>
          </div>

          {/* Dual Channel Popup */}
          <div style={{ width: "100%", maxWidth: 840, display: "flex", gap: 20 }}>
            {/* WhatsApp Card */}
            <div
              style={{
                flex: 1,
                backgroundColor: "rgba(34, 197, 94, 0.12)",
                border: "2px solid #22C55E",
                borderRadius: 22,
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <MessageSquare size={36} color="#4ADE80" />
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#86EFAC" }}>WhatsApp Bot 24/7</div>
                <div style={{ fontSize: 16, color: "#E2E8F0" }}>Akses download file terkirim instan.</div>
              </div>
            </div>

            {/* Email Card */}
            <div
              style={{
                flex: 1,
                backgroundColor: "rgba(59, 130, 246, 0.12)",
                border: "2px solid #3B82F6",
                borderRadius: 22,
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <Mail size={36} color="#60A5FA" />
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#93C5FD" }}>Invoice & Cadangan</div>
                <div style={{ fontSize: 16, color: "#E2E8F0" }}>Lisensi & tanda terima resmi masuk email.</div>
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* BEAT 4: EMPOWERMENT & HERO OUTRO (Frames 1080 - 1530) */}
      {frame >= 1080 && (
        <AbsoluteFill
          style={{
            backgroundColor: greenForest,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 40px",
          }}
        >
          {/* Logo with Emerald Glow */}
          <div
            style={{
              padding: 24,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: `3px solid ${goldAccent}`,
              boxShadow: "0 0 50px rgba(16, 185, 129, 0.45), 0 20px 45px rgba(0,0,0,0.4)",
              transform: `scale(${spring({
                frame: frame - 1080,
                fps,
                config: { damping: 14, stiffness: 120 },
              })})`,
              marginBottom: 30,
            }}
          >
            <Img
              src={staticFile("nixlev_logo.png")}
              style={{
                width: 170,
                height: 170,
                objectFit: "contain",
              }}
            />
          </div>

          <h1
            style={{
              fontSize: 64,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.15,
              marginBottom: 25,
            }}
          >
            Semua Kendali & Pendapatan <br />
            <span style={{ color: "#34D399" }}>100% Milikmu.</span>
          </h1>

          <p
            style={{
              fontSize: 26,
              color: "#CBD5E1",
              textAlign: "center",
              maxWidth: 780,
              lineHeight: 1.4,
              marginBottom: 40,
            }}
          >
            Selamat datang di era baru bisnis digital. Bikin tokomu gratis dalam 5 menit.
          </p>

          {/* CTA Button */}
          <div
            style={{
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              padding: "24px 56px",
              borderRadius: 32,
              fontSize: 32,
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              gap: 18,
              boxShadow: "0 20px 50px rgba(16, 185, 129, 0.4)",
            }}
          >
            <span>Buka Toko Gratis di nixlev.com</span>
            <ArrowRight size={36} color="#FFFFFF" />
          </div>
        </AbsoluteFill>
      )}

      {/* DYNAMIC SUBTITLES AT STRICT SAFE ZONE (bottom: 220px) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 220,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            padding: "0 40px",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(11, 26, 18, 0.92)",
              backdropFilter: "blur(12px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.16)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.4)",
              maxWidth: 920,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span style={{ fontSize: 30, fontWeight: 800, color: "#FFFFFF" }}>
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span style={{ color: "#34D399" }}>{currentSub.highlight}</span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
